import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { removeAllRecentKeyword, setRecentKeyword } from "../../redux/reducer/recentKeywordReducer";
import { findProduct } from "../../services/productServices";
import { cldResize } from "../../utils";
import { formatVnd } from "../../utils/format";

// Từ khóa khách hay tìm (giữ nguyên danh sách cũ trên header)
export const TRENDING_KEYWORDS = [
  "sắt", "perdays", "axit folic", "mẹ bầu", "vitamin tổng hợp",
  "lifespace", "men vi sinh", "cải thiện thị lực", "mờ thâm", "tiêu hóa",
];

/** Gợi ý sản phẩm theo từ khóa (debounce 300ms). */
const useProductSearch = () => {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const run = useMemo(
    () =>
      debounce(async (value) => {
        const id = ++reqId.current;
        setLoading(true);
        try {
          const res = await findProduct({ params: { keyword: value, limits: 6 } });
          if (id === reqId.current) setResults(Array.isArray(res) ? res : []);
        } catch (_) {
          if (id === reqId.current) setResults([]);
        } finally {
          if (id === reqId.current) setLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => () => run.cancel(), [run]);

  const onChange = useCallback(
    (value) => {
      setKeyword(value);
      if (!value.trim()) {
        run.cancel();
        reqId.current++;
        setResults([]);
        setLoading(false);
        return;
      }
      run(value.trim());
    },
    [run]
  );

  return { keyword, setKeyword: onChange, results, loading };
};

/** Nội dung gợi ý dùng chung cho dropdown desktop và sheet mobile. */
const SuggestionPanel = ({ keyword, results, loading, recentKeywords, onPick, onClearRecent }) => {
  const trimmed = keyword.trim();

  if (!trimmed) {
    return (
      <div className="flex flex-col gap-5 p-4">
        {recentKeywords?.length > 0 && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="m-0 text-base font-semibold text-slate-800">Tìm kiếm gần đây</h3>
              <button type="button" onClick={onClearRecent} className="text-sm font-medium text-slate-500 hover:text-sale-600">
                Xóa lịch sử
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recentKeywords.slice(0, 8).map((kw) => (
                <button
                  key={kw}
                  type="button"
                  onClick={() => onPick(kw)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-base text-slate-700 transition-colors hover:bg-slate-200"
                >
                  <i className="bi bi-clock-history text-sm text-slate-400" aria-hidden="true" />
                  {kw}
                </button>
              ))}
            </div>
          </section>
        )}
        <section>
          <h3 className="m-0 mb-2 text-base font-semibold text-slate-800">Xu hướng tìm kiếm</h3>
          <div className="flex flex-wrap gap-2">
            {TRENDING_KEYWORDS.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => onPick(kw)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-base text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
              >
                <i className="bi bi-graph-up-arrow text-sm text-brand-500" aria-hidden="true" />
                {kw}
              </button>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="py-2" aria-live="polite">
      {loading && results.length === 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex list-none items-center gap-3 rounded-lg p-2">
              <div className="h-12 w-12 flex-shrink-0 animate-pulse rounded-lg bg-slate-100" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
                <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
              </div>
            </li>
          ))}
        </ul>
      ) : results.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <i className="bi bi-search text-2xl text-slate-300" aria-hidden="true" />
          <p className="m-0 mt-2 text-base font-medium text-slate-700">Không tìm thấy sản phẩm phù hợp</p>
          <p className="m-0 mt-1 text-sm text-slate-500">Thử từ khóa khác, ví dụ: “vitamin”, “DHA”, “men vi sinh”</p>
        </div>
      ) : (
        <ul className="m-0 flex list-none flex-col p-2">
          {results.map((item) => (
            <li key={item?._id || item?.url} className="list-none">
              <Link
                to={`/${item?.category}/${item?.url}`}
                onClick={() => onPick(null)}
                className="flex items-center gap-3 rounded-lg p-2 text-inherit no-underline transition-colors hover:bg-slate-50"
              >
                <img
                  src={cldResize(item?.images?.[0], 120)}
                  alt=""
                  width={48}
                  height={48}
                  className="h-12 w-12 flex-shrink-0 rounded-lg border border-slate-100 bg-white object-contain p-0.5"
                />
                <div className="min-w-0 flex-1">
                  <p className="m-0 line-clamp-2 text-base leading-snug text-slate-800">{item?.name}</p>
                  {(item?.discountPrice || item?.salePrice) > 0 && (
                    <span className="text-base font-semibold text-sale-600">{formatVnd(item?.discountPrice || item?.salePrice)}</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-slate-100 px-2 pt-2">
        <button
          type="button"
          onClick={() => onPick(trimmed)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-base font-semibold text-brand-700 transition-colors hover:bg-brand-50"
        >
          Xem tất cả kết quả cho “{trimmed}”
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

/** Ô tìm kiếm header. variant="desktop" → dropdown; variant="mobile" → nút mở sheet toàn màn hình. */
const SearchBox = ({ variant = "desktop" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recentKeywords } = useSelector((state) => state?.recentKeywordReducer);
  const { keyword, setKeyword, results, loading } = useProductSearch();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Chọn từ khóa → trang kết quả; null = đã bấm vào 1 sản phẩm.
  const pick = useCallback(
    (kw) => {
      if (kw) {
        dispatch(setRecentKeyword(kw));
        navigate(`/tim-kiem/${encodeURIComponent(kw)}`);
      }
      setKeyword("");
      close();
      inputRef.current?.blur();
    },
    [dispatch, navigate, setKeyword, close]
  );

  // Đóng dropdown khi bấm ra ngoài / nhấn Esc
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (variant === "desktop" && wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close, variant]);

  // Khóa cuộn trang khi mở sheet mobile
  useEffect(() => {
    if (variant !== "mobile" || !open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [variant, open]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (keyword.trim()) pick(keyword.trim());
  };

  const panel = (
    <SuggestionPanel
      keyword={keyword}
      results={results}
      loading={loading}
      recentKeywords={recentKeywords}
      onPick={pick}
      onClearRecent={() => dispatch(removeAllRecentKeyword([]))}
    />
  );

  const input = (
    <form role="search" onSubmit={onSubmit} className="relative flex w-full items-center">
      <label htmlFor={`search-${variant}`} className="sr-only">
        Tìm sản phẩm
      </label>
      <i className="bi bi-search pointer-events-none absolute left-4 text-lg text-slate-400" aria-hidden="true" />
      <input
        id={`search-${variant}`}
        ref={inputRef}
        type="search"
        autoComplete="off"
        spellCheck={false}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder="Tìm vitamin, DHA, men vi sinh, thương hiệu..."
        className="h-11 w-full rounded-full border-0 bg-white pl-11 pr-24 text-lg text-slate-800 shadow-sm outline-none ring-1 ring-black/5 placeholder:text-slate-400 focus:ring-2 focus:ring-brand-300 md:text-base"
      />
      <button
        type="submit"
        className="absolute right-1 inline-flex h-9 items-center rounded-full bg-brand-600 px-4 text-base font-semibold text-white transition-colors hover:bg-brand-700"
      >
        Tìm
      </button>
    </form>
  );

  if (variant === "mobile") {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-10 w-full items-center gap-2 rounded-full bg-white px-4 text-left text-base text-slate-400 shadow-sm"
          aria-label="Mở tìm kiếm"
        >
          <i className="bi bi-search text-lg" aria-hidden="true" />
          <span className="truncate">Tìm vitamin, DHA, men vi sinh...</span>
        </button>
        {open && (
          <div className="fixed inset-0 z-[1000] flex animate-fade-in flex-col bg-white" role="dialog" aria-modal="true" aria-label="Tìm kiếm">
            <div className="flex items-center gap-2 bg-brand-600 px-3 py-2.5">
              <button
                type="button"
                onClick={close}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white hover:bg-white/10"
                aria-label="Đóng tìm kiếm"
              >
                <i className="bi bi-arrow-left text-xl" aria-hidden="true" />
              </button>
              {input}
            </div>
            <div className="flex-1 overflow-y-auto">{panel}</div>
          </div>
        )}
      </>
    );
  }

  return (
    <div ref={wrapRef} className="relative w-full">
      {input}
      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-[60] max-h-[70vh] animate-fade-in overflow-y-auto rounded-2xl bg-white shadow-pop ring-1 ring-black/5">
          {panel}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
